enum BackgroundJobStatus {
  Unknown = 'unknown',
  Success = 'success',
  Failed = 'failed',
  Running = 'running',
}

const buildDevUrl = (target: string) => {
  const https = window.location.protocol === 'https:';
  return `${https ? 'https' : 'http'}://${target}`;
};

// The AWS development environment serves the same development-built bundles
// as local dev (REACT_APP_PV_ENV=development), so it can't be told apart at
// build time — detect it from where the app is actually being served and
// derive peer hostnames from the shared domain. See infra/README.md's DNS
// section for the *.development.perfectvenue.com layout.
const AWS_DEV_DOMAIN = 'development.perfectvenue.com';

const awsDevHost = (subdomain: string): string | null =>
  typeof window !== 'undefined' &&
  window.location.hostname.endsWith(`.${AWS_DEV_DOMAIN}`)
    ? `https://${subdomain}.${AWS_DEV_DOMAIN}`
    : null;

const apiHost = (pvEnv: string) => {
  if (pvEnv === 'production') {
    return 'https://api.perfectvenue.com';
  }
  if (pvEnv === 'staging') {
    return 'https://api.perfectvenue.net';
  }
  if (pvEnv === 'staging-lancelot') {
    return 'https://api-lancelot.perfectvenue.net';
  }
  if (pvEnv === 'test') {
    return 'http://localhost:4400';
  }

  return awsDevHost('api') ?? buildDevUrl('localhost:4000');
};

const appHost = (pvEnv: string) => {
  if (pvEnv === 'production') {
    return 'https://app.perfectvenue.com';
  }
  if (pvEnv === 'staging') {
    return 'https://app.perfectvenue.net';
  }
  if (pvEnv === 'staging-lancelot') {
    return 'https://app-lancelot.perfectvenue.net';
  }
  if (pvEnv === 'test') {
    return 'http://localhost:3300';
  }

  return awsDevHost('app') ?? buildDevUrl('localhost:3000');
};

const sdkHost = (pvEnv: string) => {
  if (pvEnv === 'production') {
    return 'https://js.perfectvenue.com';
  }
  if (pvEnv === 'staging') {
    return 'https://js.perfectvenue.net';
  }
  if (pvEnv === 'staging-lancelot') {
    return 'https://js-lancelot.perfectvenue.net';
  }
  if (pvEnv === 'test') {
    return 'http://localhost:3302';
  }

  return awsDevHost('js') ?? buildDevUrl('localhost:3002');
};

const embedHost = (pvEnv: string) => {
  if (pvEnv === 'production') {
    return 'https://embed.perfectvenue.com';
  }
  if (pvEnv === 'staging') {
    return 'https://embed.perfectvenue.net';
  }
  if (pvEnv === 'staging-lanclelot') {
    return 'https://embed-lancelot.perfectvenue.net';
  }
  if (pvEnv === 'test') {
    return 'http://localhost:3303';
  }

  return awsDevHost('embed') ?? buildDevUrl('localhost:3003');
};

const marketplaceHost = (pvEnv: string) => {
  if (pvEnv === 'production') {
    return 'https://book.perfectvenue.com';
  }
  if (pvEnv === 'staging') {
    return 'https://book.perfectvenue.net';
  }
  if (pvEnv === 'staging-lanclelot') {
    return 'https://book-lancelot.perfectvenue.net';
  }
  if (pvEnv === 'test') {
    return 'http://localhost:3304';
  }

  return awsDevHost('book') ?? buildDevUrl('localhost:3004');
};

const backgroundJob = async (type: string, params?: { [key: string]: any }) => {
  const pvEnv = process.env.REACT_APP_PV_ENV ?? '';
  const backgroundJobUri = `${apiHost(pvEnv)}/background_jobs`;

  const body = {
    job_type: type,
    job_params: params,
  };

  const headers = {
    'Content-Type': 'application/json',
  };

  const createJobResp = await fetch(backgroundJobUri, {
    body: JSON.stringify(body),
    headers,
    method: 'post',
    credentials: 'include',
    mode: 'cors',
  });
  const { jobId } = await createJobResp.json();

  return jobId;
};

const backgroundJobResponse = async (jobId: string) => {
  const pvEnv = process.env.REACT_APP_PV_ENV ?? '';
  const backgroundJobStatusUri = `${apiHost(pvEnv)}/background_jobs/${jobId}`;
  const backgroundJobStatusResp = await fetch(backgroundJobStatusUri, {
    method: 'get',
    credentials: 'include',
    mode: 'cors',
  });
  return backgroundJobStatusResp.json();
};

const waitForBackgroundJob = (
  jobId: string,
  successCb: (data: any) => void,
  failedCb: (data: any) => void
) => {
  let intervalId: ReturnType<typeof setInterval> | undefined;

  const stopPolling = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = undefined;
    } else {
      throw new Error('Stopped polling on already stopped interval');
    }
  };

  intervalId = setInterval(() => {
    backgroundJobResponse(jobId)
      .then((data) => {
        if (intervalId && data.status === BackgroundJobStatus.Success) {
          stopPolling();
          successCb(data);
          return;
        }

        if (intervalId && data.status === BackgroundJobStatus.Failed) {
          stopPolling();
          failedCb(data);
        }
      })
      .catch((e) => {
        stopPolling();
        throw e;
      });
  }, 2000);

  return stopPolling;
};

const waitForBackgroundJobAsync = async (
  jobId: string,
  options?: {
    signal?: AbortSignal;
  }
) => {
  const wait = async (ms: number = 1000) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  let result = await backgroundJobResponse(jobId);

  while (result.status === BackgroundJobStatus.Running) {
    if (options?.signal?.aborted) {
      return null;
    }
    // eslint-disable-next-line no-await-in-loop
    await wait(2000);
    // eslint-disable-next-line no-await-in-loop
    result = await backgroundJobResponse(jobId);
  }
  return result;
};

export {
  apiHost,
  awsDevHost,
  sdkHost,
  appHost,
  embedHost,
  marketplaceHost,
  backgroundJob,
  waitForBackgroundJob,
  waitForBackgroundJobAsync,
  BackgroundJobStatus,
};
